import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { Input } from 'reactstrap';
import { injectIntl, intlShape } from 'react-intl';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import { roundWithPrecision } from '../../../util/number';
import Icon from '../../common/Icon';
import useProductMatrixCell from '../ProductMatrixCell/useProductMatrixCell';

const ProductCollectionMatrixCell = ({
  accountStore,
  configStore,
  uiStore,
  intl,
  cellCount,
  onHandleProductQuantity,
  onRenderTooltip,
  product,
  propertyListRef,
  value,
}) => {
  const {
    blockedState,
    cellStyle,
    closePopover,
    displayQuantity,
    handleProductQuantity,
    inputRef,
    isOpen,
    openPopover,
  } = useProductMatrixCell({
    accountStore,
    configStore,
    uiStore,
    cellCount,
    onHandleProductQuantity,
    product,
    propertyListRef,
    value,
  });

  const renderPackageInput = () => {
    const sum = roundWithPrecision(
      Number(displayQuantity || 0) * product.package_size,
      3
    );

    return (
      <div className="ProductMatrixCell__package-input">
        <Input
          id={`ProductInfo-${product.id}`}
          className="ProductMatrixCell__input"
          type="number"
          name={product.id}
          value={displayQuantity}
          onFocus={openPopover}
          onBlur={closePopover}
          onChange={handleProductQuantity}
          innerRef={inputRef}
          aria-label={intl.formatMessage(globalTranslations.quantityTitle)}
          placeholder={product.package_unit_name}
          pattern="[0-9]*"
        />
        <div className="ProductMatrixCell__package-size-sum">
          ( {sum}
          <span className="ProductMatrixCell__package-stock-unit">
            {product.stock_unit}
          </span>
          )
        </div>
      </div>
    );
  };

  const renderInput = () => {
    if (product.sellInPackage) {
      return renderPackageInput();
    }

    return (
      <Input
        id={`ProductInfo-${product.id}`}
        className="ProductMatrixCell__input"
        type="number"
        name={product.id}
        value={displayQuantity}
        onFocus={openPopover}
        onBlur={closePopover}
        onChange={handleProductQuantity}
        innerRef={inputRef}
        aria-label={intl.formatMessage(globalTranslations.quantityTitle)}
        placeholder={product.stock_unit}
      />
    );
  };

  if (blockedState) {
    return (
      <div
        className={classNames(
          'ProductCollectionMatrixQuantityCell__matrix-cell',
          'ProductCollectionMatrixQuantityCell__blocked-cell-wrapper',
          `ProductCollectionMatrixQuantityCell__blocked-cell-wrapper--${blockedState}`
        )}
        style={cellStyle}
      >
        <div className="ProductCollectionMatrixQuantityCell__blocked-cell">
          <Icon name="ban" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="ProductMatrixCell ProductCollectionMatrixQuantityCell__matrix-cell"
      style={cellStyle}
    >
      {renderInput()}
      {onRenderTooltip(isOpen)}
    </div>
  );
};

ProductCollectionMatrixCell.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  intl: intlShape.isRequired,
  cellCount: PropTypes.number.isRequired,
  onHandleProductQuantity: PropTypes.func.isRequired,
  onRenderTooltip: PropTypes.func.isRequired,
  product: PropTypes.object,
  propertyListRef: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default injectIntl(
  inject(
    'accountStore',
    'configStore',
    'uiStore'
  )(observer(ProductCollectionMatrixCell))
);
